import React from 'react';
import {
  FaTextHeight,
  FaHashtag,
  FaCalendarAlt,
  FaClock,
  FaCheckCircle,
  FaFile,
  FaLink,
  FaList,
  FaInfoCircle,
} from 'react-icons/fa';

import { Box } from '../../index';

export interface ColumnTypeIconProps {
  type: 'text' | 'number' | 'date' | 'datetime' | 'boolean' | 'file' | 'url' | 'select' | 'unknown';
  className?: string;
  showTooltip?: boolean;
}

export const ColumnTypeIcon: React.FC<ColumnTypeIconProps> = ({
  type,
  className = '',
  showTooltip = false,
}) => {
  const getIconConfig = () => {
    switch (type) {
      case 'text':
        return {
          icon: FaTextHeight,
          color: '#2563eb',
          bgColor: '#eff6ff',
          label: 'Text',
          description: 'Text field',
        };
      case 'number':
        return {
          icon: FaHashtag,
          color: '#059669',
          bgColor: '#f0fdf4',
          label: 'Number',
          description: 'Numeric field',
        };
      case 'date':
        return {
          icon: FaCalendarAlt,
          color: '#9333ea',
          bgColor: '#faf5ff',
          label: 'Date',
          description: 'Date field',
        };
      case 'datetime':
        return {
          icon: FaClock,
          color: '#4f46e5',
          bgColor: '#eef2ff',
          label: 'DateTime',
          description: 'Date and time field',
        };
      case 'boolean':
        return {
          icon: FaCheckCircle,
          color: '#ea580c',
          bgColor: '#fff7ed',
          label: 'Boolean',
          description: 'True/False field',
        };
      case 'file':
        return {
          icon: FaFile,
          color: '#dc2626',
          bgColor: '#fef2f2',
          label: 'File',
          description: 'File upload',
        };
      case 'url':
        return {
          icon: FaLink,
          color: '#0891b2',
          bgColor: '#f0fdfa',
          label: 'URL',
          description: 'Link field',
        };
      case 'select':
        return {
          icon: FaList,
          color: '#0d9488',
          bgColor: '#f0fdfa',
          label: 'Select',
          description: 'Selection field',
        };
      default:
        return {
          icon: FaInfoCircle,
          color: '#4b5563',
          bgColor: '#f9fafb',
          label: 'Unknown',
          description: 'Unknown type',
        };
    }
  };

  const config = getIconConfig();
  const IconComponent = config.icon;

  return (
    <Box
      className={className}
      display="flex"
      alignItems="center"
      justifyContent="center"
      width="20px"
      height="20px"
      borderRadius="md"
      backgroundColor={config.bgColor}
      color={config.color}
      padding="2px"
      title={showTooltip ? config.description : undefined}
    >
      <IconComponent size={12} />
    </Box>
  );
};

export const detectColumnType = (value: unknown): ColumnTypeIconProps['type'] => {
  if (value === null || value === undefined) {
    return 'unknown';
  }

  if (typeof value === 'boolean') {
    return 'boolean';
  }

  if (typeof value === 'number') {
    return 'number';
  }

  if (typeof value === 'string') {
    // Check if it's a URL
    if (value.match(/^https?:\/\//)) {
      return 'url';
    }

    // Check if it's a date
    if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return 'date';
    }

    // Check if it's a datetime
    if (value.match(/^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2}/)) {
      return 'datetime';
    }

    // Check if it's a select (enum-like)
    if (value.match(/^[A-Z_]+$/)) {
      return 'select';
    }

    return 'text';
  }

  if (typeof value === 'object') {
    // Check if it's a file object
    if ('name' in value && 'size' in value) {
      return 'file';
    }
  }

  return 'unknown';
};
